/**
 * =============================================================================
 * Componente: CampaignsCarousel
 * Versão: 1.0.0 (2026-08-17)
 *
 * Descrição:
 *   Carrossel horizontal de campanhas liberadas exibido no dashboard do
 *   afiliado (/afiliado). Reaproveita o `CampaignCard` da página
 *   "Minhas Campanhas", porém sem o grid de KPIs (showKpis={false}) para
 *   economizar espaço vertical dentro do dashboard.
 *
 *   Navegação por setas (embla/shadcn Carousel). As setas ficam
 *   desabilitadas automaticamente nos extremos.
 * =============================================================================
 */
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { CampaignCard } from '@/components/affiliate/CampaignCard';
import type { MyCampaignLink } from '@/hooks/useAffiliateMyCampaigns';

interface Props {
  /** Campanhas liberadas para o afiliado (já carregadas pela página). */
  campaigns?: MyCampaignLink[];
  isLoading?: boolean;
}

export function CampaignsCarousel({ campaigns, isLoading }: Props) {
  // Sem campanhas e sem carregamento em curso: não ocupa espaço na tela.
  if (!isLoading && (campaigns?.length ?? 0) === 0) return null;

  return (
    <section aria-label="Minhas campanhas" className="space-y-3">
      {/* Cabeçalho editorial no mesmo padrão das demais seções */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aff-muted-foreground))]">
          Minhas Campanhas
        </h3>
        <Link
          to="/afiliado/links"
          className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--aff-primary))] hover:underline"
        >
          Ver todas
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[320px] w-full rounded-2xl" />
          ))}
        </div>
      ) : (
        <Carousel opts={{ align: 'start', loop: false }} className="w-full">
          <CarouselContent className="-ml-4">
            {(campaigns ?? []).map((c) => (
              <CarouselItem
                key={c.campaign_id}
                className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
              >
                {/* KPIs ocultos: o dashboard já exibe as métricas agregadas */}
                <CampaignCard campaign={c} showKpis={false} />
              </CarouselItem>
            ))}
          </CarouselContent>
          {/* Setas de navegação — ocultas no mobile (swipe nativo) */}
          <CarouselPrevious className="hidden sm:flex -left-3" />
          <CarouselNext className="hidden sm:flex -right-3" />
        </Carousel>
      )}
    </section>
  );
}
